import {
  BankOutlined,
  CloseOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LockOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Space,
  Table,
  Tag,
  Typography,
  Checkbox,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import db from "../../db/database";
import { getAllAperturasDeCaja } from "../../services/aperturaDeCajaService";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import { getCajaByNoOrId } from "../../services/cajaService";
import {
  addCierreDeCaja,
  addCierreDetalle,
  cerrarAperturaDelCierre,
  updateCierreDeCaja,
} from "../../services/cierreDeCajaService";
import {
  getPaymentLinesByDate,
  getPaymentLinesByDateTimeRange,
} from "../../services/invoicePaymentService";
import { syncAfterCierreDeCaja } from "../../services/syncService";
import { getTPVByNoOrId } from "../../services/tpvService";
import { getAllUsers } from "../../services/userService";
import formaDePagoService from "../../services/formaDePagoService";
import { formasDePagoParaCobrar } from "../../services/formaDePagoPorTiendaSyncService";
import DenominacionesCalculator from "../common/DenominacionesCalculator";
import "./CierreDeCajaStyles.css";
import CierreTicket from "./CierreTicket";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry, parseNumberByCountry } from "../../utils/formatNumbers";
import { campoDeMoneda } from "../../utils/campoDeMoneda";
import { hayQueAvisarDeCierreSinVentas, motivoDelCierreEnCero } from "./cierreSinVentas";
import {
  toCents,
  fromCents,
  addMoney,
  sumMoney,
  resolveMoneyDecimals,
} from "../../utils/money";
import { margenDelArqueo } from "../../services/margenDelArqueo";
import { autorizacionDelArqueo } from "./autorizacionDelArqueo";
import { getDocumentTypes, getDocumentTypeSign } from "../../constants/documentType";
import { resumirElTurno } from "../../utils/resumenDelTurno";
import { buildShowTotal } from "../../utils/listPagination";
import TipoDePagoTag from "../common/TipoDePagoTag";
import {
  actualizarLinea,
  agregarLinea,
  lineaVacia,
  normalizeAmount,
  quitarLinea,
  totalDeLineas,
} from "./lineasDelDetalle";

const TIPO_PAGO_COLORS = ["green", "blue", "orange", "purple", "cyan", "magenta"];

// Esperado, Digitado y Diferencia van uno debajo del otro, con la etiqueta a la
// izquierda: así el importe tiene todo el ancho de la tarjeta y caben los cinco
// decimales de Costa Rica.
const CAMPO_EN_LINEA = {
  layout: "horizontal",
  labelAlign: "left",
  labelCol: { flex: "96px" },
  wrapperCol: { flex: "auto" },
};

const { Option } = Select;

const CierreDeCajaForm = ({ cierre, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tempDetalles, setTempDetalles] = useState([]); // Para detalles antes de guardar
  const [error, setError] = useState("");
  const [saveButtonText, setSaveButtonText] = useState("Guardar");
  const [users, setUsers] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  // Sólo las formas de pago configuradas para la tienda de esta caja
  // (FormasPagoTienda), para no llenar "Cuadre de caja" con las de todas las
  // demás. `formasPago` (arriba) se mantiene completo: lo siguen necesitando
  // el resumen por tipo de pago y la clasificación de cualquier pago, incluido
  // uno cobrado en otra tienda.
  const [formasPagoDeLaTienda, setFormasPagoDeLaTienda] = useState([]);
  const [ultimaApertura, setUltimaApertura] = useState(null);
  const [pagosPorFecha, setPagosPorFecha] = useState([]);
  const [aperturaActiva, setAperturaActiva] = useState(null);
  const [aperturaTexto, setAperturaTexto] = useState("");
  const [syncingModal, setSyncingModal] = useState(false);
  const [syncingStatus, setSyncingStatus] = useState("");
  const [arqueoEfectivoUS, setArqueoEfectivoUS] = useState(0);
  const [arqueoTarjetaUS, setArqueoTarjetaUS] = useState(0);
  const [adminAuthModal, setAdminAuthModal] = useState(false);
  const [adminForm] = Form.useForm();
  const [adminAuthLoading, setAdminAuthLoading] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [ticketVisible, setTicketVisible] = useState(false);
  const [savedCierreForTicket, setSavedCierreForTicket] = useState(null);
  const [cajaInfo, setCajaInfo] = useState(null);
  const [tpvInfo, setTpvInfo] = useState(null);
  const [cierreHistorico, setCierreHistorico] = useState(false);
  const [detalleModal, setDetalleModal] = useState({
    open: false,
    methodCode: null,
    rows: [],
  });
  const closeDetalleModal = () => setDetalleModal({ open: false, methodCode: null, rows: [] });
  const [cuadrePorMetodo, setCuadrePorMetodo] = useState({});
  const [pantallaCompleta, setPantallaCompleta] = useState(false);

  // Esc sale de la pantalla completa, salvo que haya un modal abierto: ese Esc
  // es del modal.
  useEffect(() => {
    if (!pantallaCompleta) return undefined;
    const alPresionar = (evento) => {
      if (evento.key !== "Escape") return;
      const hayModalAbierto = [...document.querySelectorAll(".ant-modal-wrap")].some(
        (modal) => modal.style.display !== "none"
      );
      if (!hayModalAbierto) setPantallaCompleta(false);
    };
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [pantallaCompleta]);
  const selectedStartDate = Form.useWatch("FechaDeApertura", form);
  const cierreHistoricoChecked = Form.useWatch("CierreHistorico", form);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });
  const [cuadrePagination, setCuadrePagination] = useState({
    current: 1,
    pageSize: 5,
  });
  const { currentUser } = useAuth();
  const { country } = useConfig();

  const importeApertura = Form.useWatch("ImporteDeApertura", form) ?? 0;

  const formatMetodoCode = (value) => (value || "").trim().toUpperCase();

  const isEditing = !!cierre?.id;

  const cargarPagosPorFecha = useCallback(
    async (fecha) => {
      try {
        const pagos = await getPaymentLinesByDate(fecha, country);
        setPagosPorFecha(pagos);
      } catch (error) {
        console.error("Error al cargar pagos por fecha:", error);
        window.messageApi.error(
          "No se pudieron cargar los pagos de facturas para la fecha seleccionada"
        );
      }
    },
    [country]
  );

  // Nueva función para cargar pagos por rango de fecha y hora
  const cargarPagosPorRangoFechaHora = useCallback(
    async (aperturaDateTime, cierreDateTime = null, historicos = false) => {
      try {
        const pagos = await getPaymentLinesByDateTimeRange(
          aperturaDateTime,
          cierreDateTime,
          historicos,
          country
        );
        setPagosPorFecha(pagos);
      } catch (error) {
        console.error("Error al cargar pagos por rango de fecha y hora:", error);
        window.messageApi.error(
          "No se pudieron cargar los pagos de facturas para el rango de tiempo seleccionado"
        );
      }
    },
    [country]
  );

  useEffect(() => {
    // Cargar usuarios y la última apertura de caja
    const cargarDatos = async () => {
      try {
        setLoading(true);

        // Obtener todos los usuarios
        const usuariosData = await getAllUsers();
        setUsers(usuariosData);
        const formasPagoData = await formaDePagoService.getAll();
        setFormasPago(formasPagoData || []);
        setFormasPagoDeLaTienda(await formasDePagoParaCobrar(formasPagoData || []));

        // Si está en modo histórico y el usuario eligió fecha
        if (cierreHistoricoChecked && selectedStartDate) {
          const aperturaDateTime = dayjs(selectedStartDate).startOf("day").toDate();

          // Fecha de cierre = última hora de esa fecha
          const cierreDateTime = dayjs(selectedStartDate).endOf("day").toDate();

          await cargarPagosPorRangoFechaHora(
            aperturaDateTime,
            cierreDateTime,
            cierreHistoricoChecked
          );
          return;
        }

        // Obtener información sobre la caja actualmente abierta (cuando NO es histórico)
        const cajaActiva = await getAperturaActiva();
        setAperturaActiva(cajaActiva);

        if (cajaActiva) {
          setAperturaTexto(
            cajaActiva.id
              ? `${cajaActiva.id} - ${
                  new Date(cajaActiva.FechaDeApertura).toISOString().split("T")[0]
                }`
              : "Pendiente"
          );

          // Si hay una caja activa, usarla como apertura relacionada
          if (cajaActiva.FechaDeApertura && cajaActiva.HoraDeApertura) {
            const fechaApertura = dayjs(cajaActiva.FechaDeApertura).format("YYYY-MM-DD");
            const horaApertura = cajaActiva.HoraDeApertura || "00:00";
            const aperturaDateTime = dayjs(
              `${fechaApertura} ${horaApertura}`,
              "YYYY-MM-DD HH:mm"
            ).toDate();

            // Usar la nueva función que considera el rango de fecha y hora
            await cargarPagosPorRangoFechaHora(aperturaDateTime, null, cierreHistoricoChecked);
          } else if (cajaActiva.FechaDeApertura) {
            // Fallback: usar solo fecha si no hay hora
            await cargarPagosPorFecha(cajaActiva.FechaDeApertura);
          }
        } else {
          // Si no hay caja activa pero hay aperturas previas
          // Ordenar por fecha en orden descendente
          const aperturas = await getAllAperturasDeCaja();
          if (aperturas && aperturas.length > 0) {
            const ultimaAperturaData = aperturas.sort(
              (a, b) => new Date(b.FechaDeApertura) - new Date(a.FechaDeApertura)
            )[0];
            setUltimaApertura(ultimaAperturaData);

            // Si hay una última apertura, cargar los pagos con fecha y hora si están disponibles
            if (ultimaAperturaData.HoraDeApertura) {
              const fechaApertura = dayjs(ultimaAperturaData.FechaDeApertura).format("YYYY-MM-DD");
              const horaApertura = ultimaAperturaData.HoraDeApertura || "00:00";
              const aperturaDateTime = dayjs(
                `${fechaApertura} ${horaApertura}`,
                "YYYY-MM-DD HH:mm"
              ).toDate();

              // Usar la nueva función que considera el rango de fecha y hora
              await cargarPagosPorRangoFechaHora(aperturaDateTime, null, cierreHistoricoChecked);
            } else {
              // Fallback: usar solo fecha si no hay hora
              await cargarPagosPorFecha(ultimaAperturaData.FechaDeApertura);
            }
          }
        }
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error);
        window.messageApi.error("Error al cargar datos necesarios");
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [
    selectedStartDate,
    cierreHistoricoChecked,
    cargarPagosPorFecha,
    cargarPagosPorRangoFechaHora,
  ]);

  // Efecto liviano y separado del de abajo: solo decide el mensaje de error,
  // sin llamar form.setFieldsValue. Se espera a que termine la carga inicial
  // (loading) para no mostrar el error en el instante en que aperturaActiva
  // todavía está en null porque el fetch de getAperturaActiva() no resolvió.
  // Debe quedar aparte del efecto pesado de abajo: ese efecto llama
  // form.setFieldsValue({FechaDeApertura: dayjs(), ...}) en todas sus ramas
  // (objeto nuevo en cada corrida), lo que cambia selectedStartDate y
  // re-dispara el efecto de carga (que depende de selectedStartDate) -- si
  // este efecto de error también dependiera de loading y viviera en el mismo
  // efecto pesado, ese ida-y-vuelta se vuelve un loop infinito (confirmado en
  // vivo: >80 ciclos en 15s antes de este fix).
  useEffect(() => {
    if (!isEditing && !loading && !aperturaActiva) {
      setError(
        "No hay una caja abierta actualmente. Debe abrir una caja antes de realizar un cierre."
      );
    } else {
      setError("");
    }
  }, [isEditing, loading, aperturaActiva]);

  useEffect(() => {
    // Si estamos editando un cierre existente, cargar sus datos
    if (isEditing && cierre) {
      setSaveButtonText(cierre.Estado === "Cerrado" ? "Cerrar caja" : "Actualizar cierre");

      setArqueoEfectivoUS(cierre.ArqueoEfectivoUS || 0);
      setArqueoTarjetaUS(cierre.ArqueoTarjetaUS || 0);

      form.setFieldsValue({
        Cajero: cierre.Cajero,
        ImporteDeApertura: cierre.ImporteDeApertura,
        FechaDeApertura: cierre.FechaDeApertura ? dayjs(cierre.FechaDeApertura) : dayjs(),
        ImporteDeCierreUS: cierre.ImporteDeCierreUS,
        ArqueoEfectivoUS: cierre.ArqueoEfectivoUS,
        ArqueoTarjetaUS: cierre.ArqueoTarjetaUS,
        FechaDeCierre: cierre.FechaDeCierre ? dayjs(cierre.FechaDeCierre) : null,
        Estado: cierre.Estado || "Abierto",
        idApertura: cierre.idApertura,
        Comprobante_Tarjetas: cierre.Comprobante_Tarjetas || "",
      });
      // Cargar pagos para la fecha de apertura
      // En modo edición, usar el rango desde apertura hasta cierre
      if (cierre.FechaDeApertura) {
        if (aperturaActiva && aperturaActiva.HoraDeApertura) {
          // Si hay apertura activa con hora, usar rango de fecha y hora
          const fechaApertura = dayjs(cierre.FechaDeApertura).format("YYYY-MM-DD");
          const horaApertura = aperturaActiva.HoraDeApertura || "00:00";
          const aperturaDateTime = dayjs(
            `${fechaApertura} ${horaApertura}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();

          // Para edición, usar hasta la fecha de cierre si existe
          let cierreDateTime = null;
          if (cierre.FechaDeCierre) {
            cierreDateTime = dayjs(cierre.FechaDeCierre).endOf("day").toDate();
          }

          cargarPagosPorRangoFechaHora(aperturaDateTime, cierreDateTime, cierreHistoricoChecked);
        } else {
          // Fallback: usar solo fecha
          cargarPagosPorFecha(cierre.FechaDeApertura);
        }
      }
      // Limpiar detalles temporales si estamos editando
      setTempDetalles([]);
    } else {
      // Si es un nuevo cierre, establecer valores por defecto basados en la caja activa
      if (aperturaActiva) {
        form.setFieldsValue({
          Cajero: aperturaActiva.Cajero,
          FechaDeApertura: aperturaActiva.FechaDeApertura
            ? dayjs(aperturaActiva.FechaDeApertura)
            : dayjs(),
          ImporteDeApertura: aperturaActiva.ImporteDeApertura || 0,
          Estado: "Cerrado", // Siempre cerrado para nuevos cierres
          FechaDeCierre: dayjs(), // Fecha actual
          ImporteDeCierreUS: 0, // Se calculará automáticamente
          ArqueoEfectivoUS: 0,
          ArqueoTarjetaUS: 0,
          idApertura: aperturaActiva.id, // No (número) de la apertura relacionada
          Comprobante_Tarjetas: "",
        });
      } else if (ultimaApertura) {
        // Si no hay apertura activa, usar datos de la última apertura como referencia
        form.setFieldsValue({
          Cajero: ultimaApertura.Cajero,
          FechaDeApertura: ultimaApertura.FechaDeApertura
            ? dayjs(ultimaApertura.FechaDeApertura)
            : dayjs(),
          ImporteDeApertura: ultimaApertura.ImporteDeApertura || 0,
          Estado: "Cerrado", // Siempre cerrado para nuevos cierres
          FechaDeCierre: dayjs(), // Fecha actual
          ImporteDeCierreUS: 0, // Se calculará automáticamente
          ArqueoEfectivoUS: 0,
          ArqueoTarjetaUS: 0,
          idApertura: ultimaApertura.id, // No (número) de la apertura relacionada
          Comprobante_Tarjetas: "",
        });
      } else {
        // Si no hay apertura previa, establecer valores por defecto básicos
        form.setFieldsValue({
          Cajero: currentUser?.No,
          FechaDeApertura: dayjs(),
          Estado: "Cerrado",
          FechaDeCierre: dayjs(),
          ImporteDeApertura: 0,
          ImporteDeCierreUS: 0, // Se calculará automáticamente
          ArqueoEfectivoUS: 0,
          ArqueoTarjetaUS: 0,
          Comprobante_Tarjetas: "",
        });
      }
    }
  }, [
    form,
    cierre,
    isEditing,
    ultimaApertura,
    currentUser,
    aperturaActiva,
    cierreHistoricoChecked,
    cargarPagosPorFecha,
    cargarPagosPorRangoFechaHora,
  ]);

  const calculatePagosTotal = useCallback(() => {
    // Calcular total de todos los pagos de facturas en unidades menores enteras
    const decimals = resolveMoneyDecimals(country);
    const totalCents = pagosPorFecha.reduce(
      (sum, pago) => sum + toCents(pago.Amount || 0, decimals),
      0
    );
    return fromCents(totalCents, decimals);
  }, [pagosPorFecha, country]);

  const tiposPago = useMemo(() => {
    const baseTypes = ["Efectivo", "Tarjeta"];
    const dbTypes = formasPago
      .map((f) => (f.Tipo_Pago || "").trim())
      .filter((t) => t && !baseTypes.some((b) => b.toUpperCase() === t.toUpperCase()));
    const unique = [...baseTypes, ...new Set(dbTypes)];
    return unique.slice(0, 6);
  }, [formasPago]);

  const getTipoPagoColor = useCallback(
    (tipoPago) => {
      const idx = tiposPago.findIndex((t) => t.toUpperCase() === (tipoPago || "").toUpperCase());
      return TIPO_PAGO_COLORS[idx >= 0 ? idx : 0];
    },
    [tiposPago]
  );

  // Calcular totales dinámicos por tipo de pago y tipo de documento
  const dynamicTotals = useMemo(() => {
    // result[tipoPago][docType.value] = sum of abs amounts
    // result[tipoPago].total = net total (applying sign)
    const result = {};
    const docTypes = getDocumentTypes(country?.code);

    tiposPago.forEach((tipo) => {
      result[tipo] = { total: 0 };
      docTypes.forEach((dt) => {
        result[tipo][dt.value] = 0;
      });
    });

    const decimals = resolveMoneyDecimals(country);

    pagosPorFecha.forEach((pago) => {
      // getPaymentLinesByDate* agrupa por factura y expone el tipo como
      // paymentMethodType; el Tipo_Pago crudo de la forma de pago no viaja.
      const tipoPago = pago.paymentMethodType || "Efectivo";
      const docType = pago.documentType || "Invoice";
      const amount = Math.abs(pago.Amount || 0);

      // Find the matching tipo (case-insensitive)
      const matchedTipo = tiposPago.find((t) => t.toUpperCase() === tipoPago.toUpperCase());
      if (!matchedTipo) return;

      if (!result[matchedTipo]) {
        result[matchedTipo] = { total: 0 };
      }

      const sign = getDocumentTypeSign(docType, country?.code);
      result[matchedTipo][docType] = addMoney(result[matchedTipo][docType] || 0, amount, decimals);
      result[matchedTipo].total = addMoney(result[matchedTipo].total, sign * amount, decimals);
    });

    return result;
  }, [pagosPorFecha, tiposPago, country]);

  const grandTotal = useMemo(
    () => sumMoney(tiposPago, (tipo) => dynamicTotals[tipo]?.total || 0, resolveMoneyDecimals(country)),
    [dynamicTotals, tiposPago, country]
  );
  const descriptionByCode = useMemo(
    () =>
      formasPago.reduce((acc, formaPago) => {
        const key = (formaPago?.Code || "").trim().toUpperCase();
        if (!key) return acc;
        acc[key] = formaPago?.Description || formaPago?.Code || key;
        return acc;
      }, {}),
    [formasPago]
  );
  const tipoPagoByCode = useMemo(
    () =>
      formasPago.reduce((acc, formaPago) => {
        const key = (formaPago?.Code || "").trim().toUpperCase();
        if (!key) return acc;
        acc[key] = formaPago?.Tipo_Pago || "";
        return acc;
      }, {}),
    [formasPago]
  );

  const getNombreFormaPago = useCallback(
    (code, fallback) => descriptionByCode[(code || "").trim().toUpperCase()] || fallback,
    [descriptionByCode]
  );
  const isMetodoEfectivo = useCallback(
    (code) => (tipoPagoByCode[formatMetodoCode(code)] || "").toUpperCase() === "EFECTIVO",
    [tipoPagoByCode]
  );

  const isMetodoTarjeta = useCallback(
    (code) =>
      formatMetodoCode(code) === "TARJETA" ||
      (tipoPagoByCode[formatMetodoCode(code)] || "").toUpperCase() === "TARJETA",
    [tipoPagoByCode]
  );

  const countryDocumentTypes = useMemo(() => getDocumentTypes(country?.code), [country?.code]);

  // Lo cobrado del turno sale de utils/resumenDelTurno.js, que es el mismo
  // módulo que lee la pantalla "Cómo va el turno". Antes se calculaba acá
  // adentro, y una consulta previa al cierre que no diera el mismo número que
  // el cierre no serviría para prepararse: es justo para lo que se consulta.
  //
  // El catálogo que se le pasa es el de la tienda (formasPagoDeLaTienda), no
  // el completo: así "Cuadre de caja" sólo muestra las formas de pago que esta
  // caja puede usar, en vez de las de todas las tiendas del país. Un pago
  // cobrado con una forma que no está en el catálogo de la tienda -- una
  // venta traída de otra tienda, o el catálogo sin sincronizar todavía --
  // igual entra: `resumirElTurno` ya agrega lo cobrado aunque el código no
  // esté en el catálogo que recibe.
  const resumen = useMemo(
    () =>
      resumirElTurno({
        pagos: pagosPorFecha,
        formasDePago: formasPagoDeLaTienda,
        importeApertura: normalizeAmount(importeApertura),
        country,
      }),
    [pagosPorFecha, formasPagoDeLaTienda, importeApertura, country]
  );

  const metodosPagoActivos = useMemo(
    () =>
      resumen.metodos.map((metodo) => ({
        code: metodo.codigo,
        nombre: metodo.nombre,
        esEfectivo: metodo.esEfectivo,
        esFondoDeApertura: metodo.esFondoDeApertura,
        cobro: metodo.cobro,
        esperado: metodo.esperado,
      })),
    [resumen]
  );

  useEffect(() => {
    let savedCuadre = {};
    if (isEditing && cierre?.CuadreMetodosPago) {
      try {
        const parsed = JSON.parse(cierre.CuadreMetodosPago);
        parsed.forEach((item) => {
          savedCuadre[item.codigoMetodoPago] = item;
        });
      } catch (e) {
        console.error("Error parsing CuadreMetodosPago:", e);
      }
    }

    setCuadrePorMetodo((prev) => {
      const next = {};
      metodosPagoActivos.forEach((metodo) => {
        const prevMetodo = prev[metodo.code] || savedCuadre[metodo.code];
        const digitado = prevMetodo?.digitado ?? 0;
        next[metodo.code] = {
          codigoMetodoPago: metodo.code,
          nombre: metodo.nombre,
          esEfectivo: metodo.esEfectivo,
          cobro: metodo.esperado,
          digitado,
          diferencia: digitado - metodo.esperado,
          detalle: prevMetodo?.detalle || [],
        };
      });
      return next;
    });
  }, [metodosPagoActivos, importeApertura, isEditing, cierre]);

  const metodoCuadreRows = useMemo(
    () =>
      metodosPagoActivos.map((metodo) => {
        const state = cuadrePorMetodo[metodo.code];
        const digitado = state?.digitado ?? 0;
        return {
          key: `metodo-${metodo.code}`,
          codigoMetodoPago: metodo.code,
          nombre: metodo.nombre,
          esEfectivo: metodo.esEfectivo,
          esFondoDeApertura: metodo.esFondoDeApertura,
          cobro: metodo.esperado,
          digitado,
          diferencia: digitado - metodo.esperado,
          detalle: state?.detalle || [],
        };
      }),
    [metodosPagoActivos, cuadrePorMetodo]
  );

  const totalCobroCuadre = metodoCuadreRows.reduce(
    (sum, row) => sum + normalizeAmount(row.cobro),
    0
  );
  const arqueoTotalDigitado = metodoCuadreRows.reduce(
    (sum, row) => sum + normalizeAmount(row.digitado),
    0
  );
  const diferenciaTotalCuadre = metodoCuadreRows.reduce(
    (sum, row) => sum + normalizeAmount(row.diferencia),
    0
  );

  // Orden de la tabla "Cuadre de caja": Efectivo siempre primero, luego el
  // resto de los métodos por importe de Cobro de mayor a menor.
  const cuadreRowsOrdenados = useMemo(() => {
    const esFilaEfectivo = (row) =>
      row.esEfectivo || formatMetodoCode(row.codigoMetodoPago) === "EFECTIVO";
    return [...metodoCuadreRows].sort((a, b) => {
      const aEfectivo = esFilaEfectivo(a);
      const bEfectivo = esFilaEfectivo(b);
      if (aEfectivo !== bEfectivo) return aEfectivo ? -1 : 1;
      return normalizeAmount(b.cobro) - normalizeAmount(a.cobro);
    });
  }, [metodoCuadreRows]);

  const totalDigitadoEfectivo = useMemo(() => {
    return metodoCuadreRows
      .filter((row) => row.esEfectivo)
      .reduce((sum, row) => sum + normalizeAmount(row.digitado), 0);
  }, [metodoCuadreRows]);

  const totalDigitadoTarjeta = useMemo(() => {
    return metodoCuadreRows
      .filter((row) => isMetodoTarjeta(row.codigoMetodoPago))
      .reduce((sum, row) => sum + normalizeAmount(row.digitado), 0);
  }, [metodoCuadreRows, isMetodoTarjeta]);

  useEffect(() => {
    form.setFieldsValue({
      ArqueoEfectivoUS: totalDigitadoEfectivo,
      ArqueoTarjetaUS: totalDigitadoTarjeta,
    });
  }, [totalDigitadoEfectivo, totalDigitadoTarjeta, form]);

  // Efecto para actualizar automáticamente ImporteDeCierreUS cuando cambian los pagos
  useEffect(() => {
    // Solo actualizar si no estamos en modo edición o si estamos creando un nuevo cierre
    if (!isEditing || !cierre?.id) {
      const totalPagos = calculatePagosTotal();
      form.setFieldsValue({
        ImporteDeCierreUS: totalPagos,
      });
    }
  }, [pagosPorFecha, isEditing, cierre, form, calculatePagosTotal]);

  const handleArqueoEfectivoUSChange = (nuevoArqueo) => {
    const value = normalizeAmount(nuevoArqueo);
    setCuadrePorMetodo((prev) => {
      const efectivoCode = Object.keys(prev).includes("EFECTIVO")
        ? "EFECTIVO"
        : Object.keys(prev).find((code) => isMetodoEfectivo(code));
      if (!efectivoCode) return prev;
      const current = prev[efectivoCode];
      return {
        ...prev,
        [efectivoCode]: {
          ...current,
          digitado: value,
          diferencia: value - normalizeAmount(current.cobro),
        },
      };
    });
  };

  const handleArqueoMetodoChange = (code, nuevoArqueo) => {
    const value = normalizeAmount(nuevoArqueo);
    setCuadrePorMetodo((prev) => {
      const current = prev[code];
      if (!current) return prev;
      return {
        ...prev,
        [code]: {
          ...current,
          digitado: value,
          diferencia: value - normalizeAmount(current.cobro),
        },
      };
    });
  };

  const getDetalleModalTotal = totalDeLineas;

  const openDetalleModal = (record) => {
    const methodCode = record.codigoMetodoPago;
    setDetalleModal({
      open: true,
      methodCode,
      rows: (record.detalle || []).length
        ? record.detalle.map((row, index) => ({
            key: `${methodCode}-${index}`,
            codigo: row.codigo || "",
            total: normalizeAmount(row.total),
          }))
        : [lineaVacia(methodCode)],
    });
  };

  // Las tres operaciones viven en lineasDelDetalle.js para poder probarlas.
  const updateDetalleModalRow = (index, patch) => {
    setDetalleModal((prev) => ({ ...prev, rows: actualizarLinea(prev.rows, index, patch) }));
  };

  const removeDetalleModalRow = (index) => {
    setDetalleModal((prev) => ({
      ...prev,
      rows: quitarLinea(prev.rows, index, prev.methodCode),
    }));
  };

  const addDetalleModalRow = () => {
    setDetalleModal((prev) => ({ ...prev, rows: agregarLinea(prev.rows, prev.methodCode) }));
  };

  const applyDetalleModal = () => {
    const total = getDetalleModalTotal(detalleModal.rows);
    setCuadrePorMetodo((prev) => {
      const current = prev[detalleModal.methodCode];
      if (!current) return prev;
      return {
        ...prev,
        [detalleModal.methodCode]: {
          ...current,
          digitado: total,
          diferencia: total - normalizeAmount(current.cobro),
          detalle: detalleModal.rows.map((row) => ({
            codigo: row.codigo,
            total: normalizeAmount(row.total),
          })),
        },
      };
    });
    closeDetalleModal();
  };

  // Procesar los pagos en detalles para el cierre
  const procesarPagosComoDetalles = () => {
    if (cierre?.No) return [];
    const idAperturaSeleccionada = form.getFieldValue("idApertura");
    const resumen = metodoCuadreRows.map((row, index) => ({
      id: `temp-${index}`,
      temp: true,
      idFormaPago: row.codigoMetodoPago,
      DesFormaPago: row.nombre,
      MontoPago: row.digitado,
      TipoPago: row.esEfectivo ? "Efectivo" : "Tarjeta",
      idCierre: null,
      idApertura: idAperturaSeleccionada,
    }));
    setTempDetalles(resumen);
    return resumen;
  };

  // Función para validar credenciales de administrador
  const validateAdminCredentials = async (username, password) => {
    try {
      // Buscar el usuario en la base de datos por su No (igual que en loginUser)
      const user = await db.users
        .where("No")
        .equals(username)
        .and((user) => !user.deleted) // Asegurar que no está eliminado
        .first();

      if (!user) {
        return false; // Usuario no encontrado
      }

      // Verificar contraseña
      if (user.password !== password) {
        return false; // Contraseña incorrecta
      }

      // Verificar si es administrador
      if (!user.Admin) {
        return false; // No es administrador
      }

      return true; // Usuario válido y es administrador
    } catch (error) {
      console.error("Error al validar credenciales de administrador:", error);
      return false;
    }
  };

  // Función para manejar la autorización de administrador
  const handleAdminAuth = async (adminValues) => {
    try {
      setAdminAuthLoading(true);

      const { username, password } = adminValues;

      // `await`, que faltaba.
      //
      // `validateAdminCredentials` es asíncrona, así que sin esperarla devuelve
      // una **promesa** -- y una promesa siempre es verdadera. `!promesa` era
      // siempre falso, la rama del error no se ejecutaba nunca y **cualquier
      // usuario y cualquier contraseña autorizaban el cierre**, incluidos los
      // que no existen. La función estaba bien escrita; nadie la escuchaba.
      if (!(await validateAdminCredentials(username, password))) {
        window.messageApi.error("Credenciales de administrador incorrectas");
        return;
      }

      // Si las credenciales son correctas, continuar con el cierre
      window.messageApi.success("Autorización de administrador concedida");
      setAdminAuthModal(false);
      adminForm.resetFields();
      setError(""); // Limpiar el error anterior

      // Continuar con el guardado usando los valores pendientes
      await continuarConCierre(pendingValues);
    } catch (error) {
      console.error("Error en autorización de administrador:", error);
      window.messageApi.error("Error durante la autorización");
    } finally {
      setAdminAuthLoading(false);
    }
  };

  // Función para continuar con el cierre después de la autorización
  const continuarConCierre = async (formattedValues) => {
    try {
      setLoading(true);
      let detallesACrear = tempDetalles;

      // Antes de guardar el cierre, procesar los pagos como detalles temporales
      if (!isEditing) {
        detallesACrear = procesarPagosComoDetalles();
      }

      let savedCierre;
      if (isEditing) {
        // Actualizar cierre existente
        savedCierre = await updateCierreDeCaja(cierre.id, formattedValues);
        window.messageApi.success("Cierre de caja actualizado correctamente");

        // Para ediciones, notificar directamente al componente padre
        onSave(savedCierre);
        return; // Salir temprano, no mostrar ticket
      } else {
        // Crear nuevo cierre
        savedCierre = await addCierreDeCaja(formattedValues);
        window.messageApi.success("Cierre de caja creado correctamente");

        // Guardar detalles temporales si hay alguno
        if (detallesACrear.length > 0) {
          for (const tempDetalle of detallesACrear) {
            const { id, temp, ...detalleData } = tempDetalle;
            await addCierreDetalle({
              ...detalleData,
              cierreId: savedCierre.id, // ID interno del cierre para la base de datos local
              idCierre: savedCierre.No || null, // No. del cierre para BC
              idApertura: form.getFieldValue("idApertura"),
              TipoPago: detalleData.TipoPago || "Efectivo",
            });
          }
          window.messageApi.success(`${detallesACrear.length} detalles guardados correctamente`);
        } else {
          // Guardar los pagos actuales como detalles si no hay detalles temporales
          for (const pago of pagosPorFecha) {
            await addCierreDetalle({
              cierreId: savedCierre.id, // ID interno del cierre para la base de datos local
              idCierre: savedCierre.No || null, // No. del cierre para BC (puede ser null si aún no está sincronizado)
              idApertura: form.getFieldValue("idApertura"),
              idFormaPago: pago.paymentMethodCode,
              DesFormaPago: pago.paymentMethodName,
              MontoPago: pago.Amount,
              TipoPago: pago.paymentMethodType || "Efectivo",
            });
          }
          window.messageApi.success(
            `${pagosPorFecha.length} líneas de pago guardadas correctamente`
          );
        }

        // Recién ahora que el cierre y sus detalles se guardaron con éxito,
        // cerramos la apertura relacionada. Si algo falló antes, la caja sigue abierta.
        if (formattedValues.Estado === "Cerrado") {
          await cerrarAperturaDelCierre(form.getFieldValue("idApertura"));
        }

        // Iniciar sincronización después de crear el cierre
        await handleSyncAfterCierre();
      }

      // Guardar los datos del cierre para el ticket
      setSavedCierreForTicket({
        ...savedCierre,
        detalles:
          detallesACrear.length > 0
            ? detallesACrear
            : pagosPorFecha.map((pago) => ({
                idFormaPago: pago.paymentMethodCode,
                DesFormaPago: pago.paymentMethodName,
                MontoPago: pago.Amount,
                TipoPago: pago.paymentMethodType || "Efectivo",
              })),
      });

      // Mostrar el ticket de cierre como paso intermedio
      setTicketVisible(true);

      // NO notificar al componente padre aún - esperar a que cierre el ticket
    } catch (error) {
      console.error("Error al guardar cierre de caja:", error);
      setError(error.message || "Error al procesar el cierre de caja");
    } finally {
      setLoading(false);
    }
  };

  // Función para cancelar la autorización de administrador
  const handleCancelAdminAuth = () => {
    setAdminAuthModal(false);
    adminForm.resetFields();
    setPendingValues(null);
    setLoading(false);
  };

  // Función para manejar el cierre del ticket y completar el proceso
  const handleTicketClose = () => {
    setTicketVisible(false);
    const cierreGuardado = savedCierreForTicket;
    setSavedCierreForTicket(null);

    // Ahora sí notificar al componente padre para cerrar el formulario
    if (cierreGuardado) {
      onSave(cierreGuardado);
    }
  };

  // Función para manejar la sincronización después de crear el cierre
  const handleSyncAfterCierre = async () => {
    try {
      setSyncingModal(true);
      setSyncingStatus("Sincronizando facturas pendientes, turnos, apertura y cierre…");

      const resultado = await syncAfterCierreDeCaja(country);
      // Una venta que no llegó a Business Central no va en el detalle del
      // cierre; el cierre sale igual, pero el cajero tiene que saberlo. Acuerdo
      // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H4.
      const avisos = resultado?.avisos || [];

      setSyncingStatus(
        avisos.length
          ? "Sincronización completada, con ventas que no van en el detalle del cierre"
          : "Sincronización completada exitosamente"
      );

      // Esperar un momento antes de cerrar el modal
      setTimeout(() => {
        setSyncingModal(false);
        setSyncingStatus("");
        if (avisos.length) {
          window.messageApi.warning(`Cierre de caja sincronizado. ${avisos.join(" ")}`);
        } else {
          window.messageApi.success("Cierre de caja sincronizado correctamente");
        }
      }, 2000);
    } catch (error) {
      setSyncingStatus("Error durante la sincronización: " + error.message);
      console.error("Error durante la sincronización:", error);
      window.messageApi.error("Error durante la sincronización: " + error.message);

      // Cerrar el modal después de mostrar el error
      setTimeout(() => {
        setSyncingModal(false);
        setSyncingStatus("");
      }, 3000);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Verificar si hay una caja abierta (si no estamos editando)
      if (!isEditing && !aperturaActiva) {
        setError(
          "No hay una caja abierta actualmente. Debe abrir una caja antes de realizar un cierre."
        );
        return;
      }

      setError("");
      setLoading(true);

      // Formatear fechas primero
      const formattedValues = {
        ...values,
        ArqueoEfectivoUS: arqueoEfectivoUS, // Usar el valor del estado
        ArqueoTarjetaUS: arqueoTarjetaUS, // Usar el valor del estado
        CuadreMetodosPago: JSON.stringify(
          metodoCuadreRows.map((row) => ({
            codigoMetodoPago: row.codigoMetodoPago,
            nombre: row.nombre,
            cobro: row.cobro,
            digitado: row.digitado,
            diferencia: row.diferencia,
            detalle: row.detalle || [],
            // A qué línea se le suma el fondo de apertura al mandar el cierre a
            // Business Central -- ver esFondoDeApertura en resumenDelTurno.js.
            esFondoDeApertura: row.esFondoDeApertura,
          }))
        ),
        FechaDeApertura: values.FechaDeApertura.format("YYYY-MM-DD"),
        FechaDeCierre: values.FechaDeCierre ? values.FechaDeCierre.format("YYYY-MM-DD") : null,
      };

      const importeCierre = parseFloat(values.ImporteDeCierreUS) || 0;

      // Turno sin una sola venta: se avisa antes de cerrar. El porqué de que la
      // regla mire sólo los cobros --y no también el arqueo-- está en
      // cierreSinVentas.js.
      if (hayQueAvisarDeCierreSinVentas(importeCierre)) {
        setLoading(false);
        const enMoneda = (valor) => formatNumberByCountry(valor, country, true);
        // Cerrar en cero tiene dos causas y piden dos revisiones distintas: un
        // turno quieto se explica buscando facturas sin sincronizar; uno cuyas
        // ventas se anularon, no. El aviso decía lo primero en los dos casos.
        const seAnularon = motivoDelCierreEnCero(importeCierre, pagosPorFecha.length) === "anulado";
        window.messageApi.confirm({
          title: seAnularon
            ? "¿Cerrar la caja con las ventas anuladas?"
            : "¿Cerrar la caja sin ninguna venta?",
          content: (
            <>
              <Alert
                message="Atención"
                description={
                  seAnularon
                    ? `En este turno hubo ${pagosPorFecha.length} movimientos de cobro, pero se anulan entre sí y el neto es cero. Revisa las notas de crédito del turno antes de cerrar.`
                    : "En este turno no se registró ninguna venta. Revisa que no falte alguna factura por sincronizar antes de cerrar."
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <p>Esta acción cerrará la caja con:</p>
              <ul>
                <li>
                  <strong>Ventas del turno:</strong> {enMoneda(0)}
                  {seAnularon ? ` (${pagosPorFecha.length} movimientos que se anulan)` : ""}
                </li>
                <li>
                  <strong>Fondo de apertura:</strong> {enMoneda(importeApertura)}
                </li>
                <li>
                  <strong>Arqueo contado:</strong> {enMoneda(arqueoTotalDigitado)}
                </li>
              </ul>
            </>
          ),
          okText: "Sí, cerrar sin ventas",
          cancelText: "Cancelar",
          maskClosable: false,
          onOk: async () => {
            try {
              setLoading(true);
              await procesarCierre(formattedValues);
            } catch (error) {
              console.error("Error al procesar el cierre sin ventas:", error);
              setError(error.message || "Error al procesar el cierre de caja");
            } finally {
              setLoading(false);
            }
          },
        });
        return;
      }

      // Si no es cierre en 0, proceder con la validación normal
      await procesarCierre(formattedValues);
    } catch (error) {
      console.error("Error al guardar cierre de caja:", error);
      setError(error.message || "Error al procesar el cierre de caja");
    } finally {
      setLoading(false);
    }
  };

  // Función separada para procesar el cierre después de las validaciones
  const procesarCierre = async (formattedValues) => {
    try {
      const caja = await getCajaByNoOrId(aperturaActiva.Caja);
      const tpv = await getTPVByNoOrId(caja.TPV);
      setCajaInfo(caja);
      setTpvInfo(tpv);
      // El umbral vive en `services/margenDelArqueo.js`, compartido con el
      // listado y el ticket. Antes esta cuenta estaba acá y los otros dos
      // pintaban con un 0,01 literal, así que un cierre dentro de su margen
      // salía en rojo igual.
      const margen = margenDelArqueo(tpv);

      // Validación de diferencia por forma de pago, igual para todos los países.
      // El "cobro" esperado de Efectivo ya incluye el importe de apertura (fondo inicial),
      // y lo digitado de Efectivo es el conteo físico del cajón (también apertura-inclusive),
      // así que ambos lados quedan comparables. Se valida cada forma de pago por separado
      // -y no solo el total general- para que una diferencia en una forma de pago no quede
      // tapada por una diferencia opuesta en otra (ej. falta efectivo pero sobra tarjeta).
      const decimals = resolveMoneyDecimals(country);
      const umbralCents = toCents(margen, decimals);

      // La regla de cuándo se pide vive en `autorizacionDelArqueo.js`, con sus
      // pruebas. Estaba suelta acá y sin ninguna: la única que existía vigilaba
      // que las credenciales se comprobaran con `await`, no *cuándo* se piden.
      // Un turno que pasó sin un solo movimiento pide supervisor aunque el
      // arqueo cuadre: requisito del cliente. Pero que el NETO dé cero no basta
      // --una factura y su nota de crédito por el mismo importe también suman
      // cero, y eso es operación normal--, así que se mira si hubo movimiento.
      const turnoSinMovimiento =
        motivoDelCierreEnCero(
          parseFloat(formattedValues?.ImporteDeCierreUS) || 0,
          pagosPorFecha.length
        ) === "sinMovimiento";
      const autorizacion = autorizacionDelArqueo(
        metodoCuadreRows,
        tpv,
        decimals,
        arqueoTotalDigitado,
        turnoSinMovimiento
      );

      if (autorizacion.hayQuePedirla) {
        setPendingValues(formattedValues);
        // El motivo cambia lo que se le explica al cajero: una diferencia se
        // puede nombrar; un cuadre vacío es otra cosa y merece decirse tal cual.
        setError(
          autorizacion.motivo === "sinCuadre"
            ? `Se requiere autorización de administrador: se contó ${formatNumberByCountry(arqueoTotalDigitado, country, true)} en el arqueo y no hay ninguna forma de pago contra la que cuadrarlo. Revisa que el catálogo de formas de pago esté sincronizado.`
            : autorizacion.motivo === "sinMovimiento"
              ? "Se requiere autorización de administrador: el turno pasó sin un solo movimiento de cobro. El arqueo cuadra, pero una caja que se abre y se cierra sin que ocurra nada no la cierra un cajero solo."
              : `Se requiere autorización de administrador: la diferencia entre lo esperado y lo digitado supera el margen permitido (${formatNumberByCountry(fromCents(umbralCents, decimals), country, true)}) para este TPV en ${autorizacion.metodos.length === 1 ? "la forma de pago" : "las formas de pago"}: ${autorizacion.metodos
                  .map(
                    (row) =>
                      `${(row.nombre || "").toString().toUpperCase()}: ${formatNumberByCountry(fromCents(row.diferenciaCents, decimals), country, true)}`
                  )
                  .join(" · ")}.`
        );
        setAdminAuthModal(true);
        setLoading(false);
        return;
      }

      // Si no hay problemas de margen, continuar directamente con el cierre
      await continuarConCierre(formattedValues);
    } catch (error) {
      console.error("Error al procesar cierre de caja:", error);
      setError(error.message || "Error al procesar el cierre de caja");
      setLoading(false);
    }
  };

  const textSorter = (dataIndex) => (a, b) =>
    (a[dataIndex] ?? "").toString().localeCompare((b[dataIndex] ?? "").toString());
  const numberSorter = (dataIndex) => (a, b) => (a[dataIndex] || 0) - (b[dataIndex] || 0);

  const detallesColumns = [
    {
      title: "Factura",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      // invoiceNo ya viene resuelto con prioridad No > Pre_Assigned_No > ID interno de Dexie
      // (ver getPaymentLinesByDate/getPaymentLinesByDateTimeRange en invoicePaymentService.js)
      render: (invoiceNo) => invoiceNo || "-",
      sorter: textSorter("invoiceNo"),
    },
    {
      title: "ID Apertura",
      dataIndex: "idApertura",
      key: "idApertura",
      sorter: textSorter("idApertura"),
    },
    {
      title: "Forma de pago",
      dataIndex: "DesFormaPago",
      key: "DesFormaPago",
      sorter: textSorter("DesFormaPago"),
    },
    {
      title: "Tipo",
      dataIndex: "TipoPago",
      key: "TipoPago",
      // Tercera copia de la misma regla, con los dos mismos defectos: dos
      // colores para cinco tipos, y «Efectivo» supuesto cuando no se sabe. Ahora
      // sale del componente compartido, como el maestro y el cobro.
      render: (tipoPago) => <TipoDePagoTag tipo={tipoPago} />,
      sorter: textSorter("TipoPago"),
    },
    {
      title: "Monto",
      dataIndex: "MontoPago",
      key: "MontoPago",
      render: (monto) => {
        let validMonto = monto || 0;
        return formatNumberByCountry(validMonto, country, true);
      },
      sorter: numberSorter("MontoPago"),
    },
  ];

  const cuadreColumns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      width: "24%",
      ellipsis: true,
      render: (value) => (
        <span className="cierre-cuadre-label">{(value ?? "").toString().toUpperCase()}</span>
      ),
      sorter: textSorter("nombre"),
    },
    {
      title: "Cobro",
      dataIndex: "cobro",
      key: "cobro",
      width: "18%",
      render: (value) => (
        <div className="cierre-cuadre-value cierre-cuadre-center">
          {formatNumberByCountry(value || 0, country, true)}
        </div>
      ),
      sorter: numberSorter("cobro"),
    },
    {
      title: "Digitado",
      dataIndex: "digitado",
      key: "digitado",
      width: "20%",
      sorter: numberSorter("digitado"),
      render: (_, record) => {
        if (record.esTotalRow) {
          return (
            <div className="cierre-cuadre-value cierre-cuadre-total cierre-cuadre-center">
              {formatNumberByCountry(arqueoTotalDigitado, country, true)}
            </div>
          );
        }
        if (record.codigoMetodoPago.toUpperCase() === "EFECTIVO") {
          return (
            <InputNumber
              style={{ width: "100%" }}
              value={record.digitado}
              disabled
              {...campoDeMoneda(country)}
            />
          );
        }
        return (
          <InputNumber
            style={{ width: "100%" }}
            step={0.01}
            value={record.digitado}
            onChange={(value) => handleArqueoMetodoChange(record.codigoMetodoPago, value)}
            placeholder={`Digitado en ${country?.currency}`}
            disabled={cierre?.No}
            min={0}
            {...campoDeMoneda(country)}
          />
        );
      },
    },
    {
      title: "Detalle",
      key: "detalle",
      width: "20%",
      render: (_, record) => {
        if (record.esTotalRow) {
          return <div className="cierre-cuadre-center">-</div>;
        }
        if (record.codigoMetodoPago.toUpperCase() === "EFECTIVO") {
          return (
            <Space.Compact style={{ width: "100%" }}>
              <DenominacionesCalculator
                value={record.digitado}
                onChange={handleArqueoEfectivoUSChange}
                modalTitle="Arqueo Efectivo - Cálculo por Denominaciones"
                placeholder={`Arqueo Efectivo en (${country?.currency})`}
                disabled={cierre?.No}
              />
            </Space.Compact>
          );
        }
        return (
          <Space.Compact style={{ width: "100%" }}>
            <Input style={{ width: "85%" }} value="Editar" disabled />
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openDetalleModal(record)}
              disabled={cierre?.No}
              style={{ width: "15%" }}
              title="Ingresar detalle"
              aria-label="Ingresar detalle"
            />
          </Space.Compact>
        );
      },
    },
    {
      title: "Diferencia",
      dataIndex: "diferencia",
      key: "diferencia",
      width: "18%",
      sorter: numberSorter("diferencia"),
      render: (value) => {
        let statusClass = "";
        if (value !== null) {
          statusClass = value === 0 ? "cierre-cuadre-match" : "cierre-cuadre-mismatch";
        }

        return (
          <div className={`cierre-cuadre-value cierre-cuadre-center ${statusClass}`}>
            {value === null ? "-" : formatNumberByCountry(value || 0, country, true)}
          </div>
        );
      },
    },
  ];

  const renderCuadreSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <span className="cierre-cuadre-label">TOTAL</span>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        <div className="cierre-cuadre-value cierre-cuadre-center">
          {formatNumberByCountry(totalCobroCuadre || 0, country, true)}
        </div>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2}>
        <div className="cierre-cuadre-value cierre-cuadre-total cierre-cuadre-center">
          {formatNumberByCountry(arqueoTotalDigitado, country, true)}
        </div>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={3}>
        <div className="cierre-cuadre-center">-</div>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4}>
        <div
          className={`cierre-cuadre-value cierre-cuadre-center ${
            diferenciaTotalCuadre === 0 ? "cierre-cuadre-match" : "cierre-cuadre-mismatch"
          }`}
        >
          {formatNumberByCountry(diferenciaTotalCuadre || 0, country, true)}
        </div>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <div
      className={`cierreDeCaja-form-container${
        pantallaCompleta ? " cierreDeCaja-pantalla-completa" : ""
      }`}
    >
      <div className="cierreDeCaja-encabezado">
        <Typography.Title level={4}>
          {isEditing ? "Editar cierre de caja" : "Nuevo cierre de caja"}
        </Typography.Title>
        {pantallaCompleta && (
          <Space>
            <Button icon={<FullscreenExitOutlined />} onClick={() => setPantallaCompleta(false)}>
              Salir de pantalla completa
            </Button>
            {/* La X del modal que abre el cierre se esconde en pantalla completa:
                se quedaba en la esquina del modal, flotando a media pantalla.
                Esta hace lo mismo, junto a los demás botones. */}
            {onCancel && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={onCancel}
                title="Cerrar"
                aria-label="Cerrar"
              />
            )}
          </Space>
        )}
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError("")}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={!isEditing && !aperturaActiva}
      >
        <div className="cierreDeCaja-header-section">
          <h3>Información general</h3>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="Cajero"
                label="Cajero"
                rules={[{ required: true, message: "Seleccione un cajero" }]}
              >
                <Select
                  placeholder="Seleccione un cajero"
                  disabled={cierre?.No || (!isEditing && aperturaActiva)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label || "").toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {users.map((user) => (
                    <Select.Option key={user.id} value={user.No}>
                      {user.firstName} {user.lastName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="ImporteDeApertura"
                label="Importe de apertura"
                rules={[{ required: true, message: "Ingrese el importe de apertura" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled={cierre?.No || (!isEditing && aperturaActiva)}
                  {...campoDeMoneda(country)}
                  parser={(value) => parseNumberByCountry(value, country)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="FechaDeApertura"
                label="Fecha de apertura"
                rules={[
                  {
                    required: true,
                    message: "Seleccione la fecha de apertura",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabled={!cierreHistorico && (cierre?.No || (!isEditing && aperturaActiva))}
                  onChange={(date) => {
                    if (cierreHistorico && date) {
                      // Ajustar automáticamente fecha de cierre a última hora
                      form.setFieldsValue({
                        FechaDeCierre: date.endOf("day"),
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="Estado" label="Estado">
                <Select disabled>
                  <Option value="Abierto">Abierto</Option>
                  <Option value="Cerrado">Cerrado</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="FechaDeCierre" label="Fecha de cierre">
                <DatePicker disabled style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              {/* Campo oculto para guardar el valor real del idApertura */}
              <Form.Item name="idApertura" hidden={true} />
              <Form.Item label="Apertura relacionada">
                {loading ? (
                  <Input
                    placeholder="Verificando apertura..."
                    disabled={true}
                    addonBefore={<BankOutlined />}
                  />
                ) : aperturaActiva ? (
                  <Input
                    value={aperturaTexto}
                    disabled={true}
                    addonBefore={<BankOutlined />}
                    suffix={<Tag color="success">Activa</Tag>}
                  />
                ) : (
                  <Input
                    placeholder="No hay apertura activa"
                    disabled={true}
                    addonBefore={<BankOutlined />}
                    suffix={<Tag color="error">Sin apertura</Tag>}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Checkbox de cierre histórico */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="CierreHistorico" valuePropName="checked">
                <Checkbox onChange={(e) => setCierreHistorico(e.target.checked)}>
                  Cierre de caja histórico
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="ImporteDeCierreUS" hidden={true}>
            <Input />
          </Form.Item>

          <div className="cierre-section">
            <h3>Cuadre de caja</h3>
            <Table
              columns={cuadreColumns}
              dataSource={cuadreRowsOrdenados}
              pagination={{
                ...cuadrePagination,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                showTotal: buildShowTotal(cuadreRowsOrdenados.length, "elementos", country),
              }}
              onChange={(newPagination) => setCuadrePagination(newPagination)}
              summary={renderCuadreSummary}
              rowKey="key"
              size="small"
              tableLayout="fixed"
              className="cierre-cuadre-table"
            />
          </div>
          <div className="cierre-section">
            <div className="cierreDeCaja-encabezado">
              <h3>Resumen de arqueo por tipo de pago</h3>
              {!pantallaCompleta && (
                <Button
                  type="link"
                  icon={<FullscreenOutlined />}
                  onClick={() => setPantallaCompleta(true)}
                >
                  Ver a pantalla completa
                </Button>
              )}
            </div>
            <Row gutter={16}>
              {tiposPago.map((tipo, idx) => {
                const isEfectivo = tipo.toUpperCase() === "EFECTIVO";
                const isTarjeta = tipo.toUpperCase() === "TARJETA";
                const esperado = metodoCuadreRows
                  .filter((row) => {
                    const rowTipo = (
                      tipoPagoByCode[formatMetodoCode(row.codigoMetodoPago)] || ""
                    ).toUpperCase();
                    const codeMatch = formatMetodoCode(row.codigoMetodoPago) === tipo.toUpperCase();
                    return rowTipo === tipo.toUpperCase() || codeMatch;
                  })
                  .reduce((sum, row) => sum + normalizeAmount(row.cobro), 0);
                const digitado = metodoCuadreRows
                  .filter((row) => {
                    const rowTipo = (
                      tipoPagoByCode[formatMetodoCode(row.codigoMetodoPago)] || ""
                    ).toUpperCase();
                    const codeMatch = formatMetodoCode(row.codigoMetodoPago) === tipo.toUpperCase();
                    return rowTipo === tipo.toUpperCase() || codeMatch;
                  })
                  .reduce((sum, row) => sum + normalizeAmount(row.digitado), 0);
                const diff = digitado - esperado;
                // Determine the form field name for backward compatibility
                const formName = isEfectivo
                  ? "ArqueoEfectivoUS"
                  : isTarjeta
                    ? "ArqueoTarjetaUS"
                    : null;

                // De dos en dos: con tres por fila, cada importe quedaba en un
                // noveno del ancho y en colones no se alcanzaba a leer.
                return (
                  <Col xs={24} md={12} key={tipo}>
                    <div className="cierre-resumen-card">
                      <h4>
                        <Tag color={TIPO_PAGO_COLORS[idx]}>{tipo}</Tag>
                      </h4>
                      <Row gutter={8}>
                        <Col span={24}>
                          <Form.Item label="Esperado" {...CAMPO_EN_LINEA}>
                            <InputNumber
                              disabled
                              style={{ width: "100%" }}
                              value={esperado}
                              {...campoDeMoneda(country)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          {formName ? (
                            <Form.Item
                              {...CAMPO_EN_LINEA}
                              name={formName}
                              label="Digitado"
                              rules={
                                isEfectivo
                                  ? [
                                      {
                                        validator: (_, value) => {
                                          if (esperado > 0) {
                                            const valorActual = normalizeAmount(value);
                                            if (valorActual >= 0) return Promise.resolve();
                                            return Promise.reject(
                                              new Error(
                                                "El arqueo de efectivo es obligatorio cuando hay cobro en efectivo"
                                              )
                                            );
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ]
                                  : undefined
                              }
                            >
                              <InputNumber
                                disabled
                                style={{ width: "100%" }}
                                {...campoDeMoneda(country)}
                              />
                            </Form.Item>
                          ) : (
                            <Form.Item label="Digitado" {...CAMPO_EN_LINEA}>
                              <InputNumber
                                disabled
                                style={{ width: "100%" }}
                                value={digitado}
                                {...campoDeMoneda(country)}
                              />
                            </Form.Item>
                          )}
                        </Col>
                        <Col span={24}>
                          <Form.Item label="Diferencia" {...CAMPO_EN_LINEA}>
                            <InputNumber
                              disabled
                              style={{
                                width: "100%",
                                color: diff === 0 ? "green" : "red",
                                fontWeight: "bold",
                              }}
                              value={diff}
                              {...campoDeMoneda(country)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>

        <div className="cierreDeCaja-detalle-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h3>Detalles del cierre</h3>
          </div>

          <Table
            columns={detallesColumns}
            dataSource={pagosPorFecha.map((pago, index) => ({
              id: `pago-${index}`,
              idCierre: cierre?.No || "Pendiente",
              idApertura: form.getFieldValue("idApertura"),
              idFormaPago: pago.paymentMethodCode,
              DesFormaPago: pago.paymentMethodName,
              TipoPago: pago.paymentMethodType || "Efectivo",
              MontoPago: pago.Amount,
              invoiceNo: pago.invoiceNo,
              invoiceId: pago.invoiceId,
              temp: true,
            }))}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50"],
            }}
            onChange={(newPagination) => setPagination(newPagination)}
            loading={loading}
            className="cierreDeCaja-detalle-table"
          />

          {pagosPorFecha.length > 0 && (
            <div className="cierreDeCaja-totals">
              {/* Secciones dinámicas por tipo de documento */}
              {countryDocumentTypes.map((docType) => {
                const hasData = tiposPago.some(
                  (tipo) => (dynamicTotals[tipo]?.[docType.value] || 0) > 0
                );
                if (!hasData) return null;

                const subtotal = sumMoney(
                  tiposPago,
                  (tipo) => (dynamicTotals[tipo]?.[docType.value] || 0) * docType.sign,
                  resolveMoneyDecimals(country)
                );

                return (
                  <div className="cierreDeCaja-totals-section" key={docType.value}>
                    <div className="cierreDeCaja-totals-section-title">
                      <strong>{docType.label.toUpperCase()}</strong>
                    </div>
                    {tiposPago.map((tipo) => {
                      const amount = dynamicTotals[tipo]?.[docType.value] || 0;
                      return (
                        <div className="cierreDeCaja-totals-item" key={`${docType.value}-${tipo}`}>
                          <span>
                            <Tag color={getTipoPagoColor(tipo)}>{tipo}</Tag>
                          </span>
                          <span>{formatNumberByCountry(docType.sign * amount || 0, country, true)}</span>
                        </div>
                      );
                    })}
                    <div className="cierreDeCaja-totals-item subtotal">
                      <span>Subtotal {docType.label}:</span>
                      <span>{formatNumberByCountry(subtotal, country, true)}</span>
                    </div>
                  </div>
                );
              })}

              {/* Totales Finales */}
              <div className="cierreDeCaja-totals-section">
                <div className="cierreDeCaja-totals-section-title">
                  <strong>TOTALES FINALES</strong>
                </div>
                {tiposPago.map((tipo) => {
                  const total = dynamicTotals[tipo]?.total || 0;
                  return (
                    <div className="cierreDeCaja-totals-item" key={`total-${tipo}`}>
                      <span>
                        <Tag color={getTipoPagoColor(tipo)}>Total {tipo}</Tag>
                      </span>
                      <span>{formatNumberByCountry(total, country, true)}</span>
                    </div>
                  );
                })}
                <div className="cierreDeCaja-totals-item grand-total">
                  <span>TOTAL GENERAL:</span>
                  <span>{formatNumberByCountry(grandTotal, country, true)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="cierreDeCaja-form-actions">
          <Button onClick={onCancel}>Cancelar</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={cierre?.No || (!isEditing && !aperturaActiva)}
          >
            {saveButtonText}
          </Button>
        </div>
      </Form>

      {/* Modal de autorización de administrador */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LockOutlined />
            <span>Autorización de Administrador</span>
          </div>
        }
        open={adminAuthModal}
        onCancel={handleCancelAdminAuth}
        footer={null}
        centered
        maskClosable={false}
      >
        <div style={{ marginBottom: "16px" }}>
          <Alert
            message="Autorización requerida"
            description={
              error ??
              "La diferencia entre el importe de cierre y el arqueo supera el margen permitido. Se requiere autorización de administrador para continuar."
            }
            type="warning"
            showIcon
          />
        </div>

        <Form form={adminForm} layout="vertical" onFinish={handleAdminAuth}>
          <Form.Item
            name="username"
            label="Usuario"
            rules={[
              {
                required: true,
                message: "Ingrese el usuario de administrador",
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Usuario de administrador"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Contraseña"
            rules={[
              {
                required: true,
                message: "Ingrese la contraseña de administrador",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Contraseña de administrador"
              autoComplete="current-password"
            />
          </Form.Item>

          <div style={{ textAlign: "right", marginTop: "24px" }}>
            <Button onClick={handleCancelAdminAuth} style={{ marginRight: "8px" }}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={adminAuthLoading}>
              Autorizar
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal de sincronización */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CloudSyncOutlined />
            <span>Sincronizando</span>
          </div>
        }
        open={syncingModal}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", fontSize: "16px" }}>
            {syncingStatus || "Sincronizando datos..."}
          </div>
        </div>
      </Modal>

      {/* Modal del ticket de cierre */}
      {savedCierreForTicket && (
        <CierreTicket
          open={ticketVisible}
          onClose={handleTicketClose}
          cierre={savedCierreForTicket}
          detalles={savedCierreForTicket.detalles || []}
          caja={cajaInfo?.No || cajaInfo?.id}
          tpv={tpvInfo?.No || tpvInfo?.id}
        />
      )}

      <Modal
        title={`Detalle ${getNombreFormaPago(detalleModal.methodCode, detalleModal.methodCode || "")}`}
        open={detalleModal.open}
        onCancel={closeDetalleModal}
        onOk={applyDetalleModal}
        okText="Aplicar detalle"
        width={560}
      >
        <Table
          size="small"
          pagination={false}
          dataSource={detalleModal.rows}
          rowKey="key"
          columns={[
            {
              title: "Código",
              dataIndex: "codigo",
              render: (value, _, index) => (
                <Input
                  value={value}
                  onChange={(e) => updateDetalleModalRow(index, { codigo: e.target.value })}
                  placeholder="Ej: Ticket POS / Voucher / Referencia"
                />
              ),
            },
            {
              title: "Total",
              dataIndex: "total",
              render: (value, _, index) => (
                <InputNumber
                  style={{ width: "100%" }}
                  precision={country?.decimals ?? 2}
                  min={0}
                  value={value}
                  onChange={(newValue) =>
                    updateDetalleModalRow(index, { total: normalizeAmount(newValue) })
                  }
                  {...campoDeMoneda(country)}
                />
              ),
            },
            {
              title: "",
              width: 40,
              render: (_, __, index) => (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeDetalleModalRow(index)}
                />
              ),
            },
          ]}
          footer={() => (
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Button icon={<PlusOutlined />} onClick={addDetalleModalRow}>
                Agregar línea
              </Button>
              <Typography.Text strong>
                Total:{" "}
                {formatNumberByCountry(getDetalleModalTotal(detalleModal.rows), country, true)}
              </Typography.Text>
            </Space>
          )}
        />
      </Modal>
    </div>
  );
};

export default CierreDeCajaForm;
